import React, {useEffect, useState} from 'react'
import {
  StepperWrapper,
  StepperButton,
  StepperValue,
  StepperInput,
} from './QuantityStepper.styles'

/**
 * QuantityStepper
 *
 * @param {number}   quantity          - current quantity
 * @param {number}   min               - lowest allowed quantity
 * @param {number}   maxQuantity       - highest allowed quantity (null = no cap)
 * @param {number}   displayValue      - overrides the shown number (already scaled)
 * @param {number}   displayMultiplier - shown units per quantity, e.g. 5 pages
 *                                       per pack. Typed values divide by this.
 * @param {Function} onIncrement
 * @param {Function} onDecrement
 * @param {Function} onQuantityInput   - pass to make the value directly
 *                                       editable; called with the clamped
 *                                       quantity on commit. Omit for a
 *                                       read-only value.
 * @param {string}   label             - accessible name for the field
 */
export function QuantityStepper({
  quantity = 0,
  min = 0,
  maxQuantity = null,
  displayValue,
  displayMultiplier = 1,
  onIncrement,
  onDecrement,
  onQuantityInput,
  label = 'Quantity',
}) {
  const isEditable = typeof onQuantityInput === 'function'
  // Keystrokes are held locally so the field can be cleared and retyped
  // without each intermediate value dispatching a quantity change.
  const [draft, setDraft] = useState(null)

  const shownValue = displayValue ?? quantity * displayMultiplier

  // Discard a stale draft when the quantity moves underneath us — a +/- click,
  // an upgrade floor being applied, or the product being removed elsewhere.
  useEffect(() => {
    setDraft(null)
  }, [quantity])

  const commit = () => {
    if (draft === null) return
    const parsed = Number.parseInt(draft, 10)
    setDraft(null)
    // Empty or non-numeric input reverts to the current quantity.
    if (!Number.isFinite(parsed)) return

    let next = Math.round(parsed / displayMultiplier)
    if (next < min) next = min
    if (maxQuantity != null && next > maxQuantity) next = maxQuantity
    if (next === quantity) return

    onQuantityInput(next)
  }

  const handleKeyDown = e => {
    if (e.key === 'Enter') {
      e.preventDefault()
      commit()
      return
    }
    if (e.key === 'Escape') setDraft(null)
  }

  return (
    <StepperWrapper>
      <StepperButton
        type="button"
        onClick={onDecrement}
        disabled={quantity <= min}
        aria-label={`Decrease ${label}`}
      >
        −
      </StepperButton>
      {isEditable ? (
        <StepperInput
          type="number"
          inputMode="numeric"
          aria-label={label}
          value={draft ?? shownValue}
          min={min * displayMultiplier}
          max={
            maxQuantity != null ? maxQuantity * displayMultiplier : undefined
          }
          step={displayMultiplier}
          onChange={e => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={handleKeyDown}
          onFocus={e => e.target.select()}
          // The surrounding card is clickable — keep focusing the field from
          // toggling the card's selection.
          onClick={e => e.stopPropagation()}
        />
      ) : (
        <StepperValue>{shownValue}</StepperValue>
      )}
      <StepperButton
        type="button"
        onClick={onIncrement}
        disabled={maxQuantity != null && quantity >= maxQuantity}
        aria-label={`Increase ${label}`}
      >
        +
      </StepperButton>
    </StepperWrapper>
  )
}
